/**
 * Visor del ticket técnico (réplica de `.ticket-display` + `renderTicketTechnical` del SSR).
 *
 * Presentacional: recibe el texto del ticket (lo produce el motor en el backend, ya filtrado
 * por rol) y lo renderiza como líneas monoespaciadas, coloreando cada una según su tipo
 * (`parseTicket`). No usa `dangerouslySetInnerHTML` (más seguro que el SSR); sin lógica de
 * negocio ni llamadas a la API (`docs/frontend_architecture.md`, Pr. 6).
 */
import { cn } from "@/components/ui";

import { parseTicket, type TicketLineKind } from "../format";

interface TicketDisplayProps {
  ticket: string | null | undefined;
}

/** Clases Tailwind por tipo de línea (réplica de los estilos `ticket-*` del SSR). */
const LINE_CLASSES: Record<TicketLineKind, string> = {
  plain: "text-slate-700",
  "sec-a": "bg-green-100 font-semibold text-green-800",
  "sec-b": "bg-blue-100 font-semibold text-blue-900",
  subtotal: "bg-amber-50 font-semibold text-amber-900",
  total: "bg-indigo-100 font-bold text-indigo-900",
  sep: "text-slate-400",
  dim: "text-slate-600",
};

export function TicketDisplay({ ticket }: TicketDisplayProps) {
  const lines = parseTicket(ticket);
  if (lines.length === 0) {
    return null;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-[0.8rem] leading-relaxed">
      {lines.map((line, idx) => (
        <div
          key={idx}
          className={cn("whitespace-pre-wrap rounded px-2 py-0.5", LINE_CLASSES[line.kind])}
        >
          {line.text || " "}
        </div>
      ))}
    </div>
  );
}
